import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { siteLink } from "@/lib/siteLink";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { trackPageview } from "@/lib/pageview";
import siteConfig from "@/config/siteConfig";
import { LANG_CODES, DGTGLangScope } from "@/i18n/dgtgLang";
import { buildAuthUrl, getToken } from "@/lib/authUtils";
import React, { Suspense, lazy } from 'react';
import RegionBanner from '@/components/RegionBanner';
import CookieConsent from '@/components/CookieConsent';
import { reset as resetAdBudget } from '@/lib/adBudget';

// ────────────────────────────────────────────────────────────
//  ROUTE-LEVEL CODE SPLITTING (Feb 2026 — SEO/Perf batch C)
//  Eagerly loaded: only the current site's homepage + top-level nav
//    (kept import-style below to guarantee fast first paint)
//  Lazy-loaded: all secondary routes, admin panel, cross-site clusters,
//    programmatic pages, author pages, legal, submission forms.
//  Result: initial JS drops from ~490KB to ~150-200KB per domain.
// ────────────────────────────────────────────────────────────
const _l = (fn) => lazy(() => fn().catch((err) => {
  // A stale cached index.html can reference JS chunk files that no longer
  // exist after a redeploy ("Loading chunk X failed"), which otherwise leaves
  // the reader stuck on the route's loading spinner forever. Force one clean
  // reload to pick up the new build; a session flag stops any infinite loop.
  const key = 'chunk-retry-once';
  if (typeof window !== 'undefined' && !sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1');
    window.location.reload();
  }
  throw err;
}));

// Auto Website Pages (Home is eager, rest lazy)
import AutoHome from "@/pages/auto/AutoHome";
const VehicleListings = _l(() => import("@/pages/auto/VehicleListings"));
const VehicleDetail = _l(() => import("@/pages/auto/VehicleDetail"));
const CompareVehicles = _l(() => import("@/pages/auto/CompareVehicles"));
const AutoNews = _l(() => import("@/pages/auto/AutoNews"));
const AutoRegistrations = _l(() => import("@/pages/auto/AutoRegistrations"));
const AutoPricePulse = _l(() => import("@/pages/auto/AutoPricePulse"));
const AutoBlog = _l(() => import("@/pages/auto/AutoBlog"));
const AutoAuthor = _l(() => import("@/pages/auto/AutoAuthor"));
const SubmitVehicle = _l(() => import("@/pages/auto/SubmitVehicle"));

// Coupon Website Pages
import CouponHome from "@/pages/coupon/CouponHome";
const CouponListings = _l(() => import("@/pages/coupon/CouponListings"));
const CouponDetail = _l(() => import("@/pages/coupon/CouponDetail"));
const CouponBlog = _l(() => import("@/pages/coupon/CouponBlog"));
const CouponAuthor = _l(() => import("@/pages/coupon/CouponAuthor"));
const LiveDeals = _l(() => import("@/pages/coupon/LiveDeals"));

// Travel + Tech blog sites (new — niche affiliate blogs)
const TravelBlog = _l(() => import("@/pages/travel/TravelBlog"));
const TravelHome = _l(() => import("@/pages/travel/TravelHome"));
const TravelRoute = _l(() => import("@/pages/travel/TravelRoute"));
const TravelRoutesIndex = _l(() => import("@/pages/travel/TravelRoute").then((m) => ({ default: m.TravelRoutesIndex })));
const TravelDestinationsIndex = _l(() => import("@/pages/travel/TravelDestinations").then((m) => ({ default: m.TravelDestinationsIndex })));
const TravelDestination = _l(() => import("@/pages/travel/TravelDestinations").then((m) => ({ default: m.TravelDestination })));
const TravelModePage = _l(() => import("@/pages/travel/TravelDestinations").then((m) => ({ default: m.TravelModePage })));
const TravelAuthor = _l(() => import("@/pages/travel/TravelAuthor"));
const TechBlog = _l(() => import("@/pages/tech/TechBlog"));
const TechAuthor = _l(() => import("@/pages/tech/TechAuthor"));

// Finance Website Pages
import FinanceHome from "@/pages/finance/FinanceHome";
const FinanceCalculators = _l(() => import("@/pages/finance/FinanceCalculators"));
const FinanceCompare = _l(() => import("@/pages/finance/FinanceCompare"));
const FinanceFunds = _l(() => import("@/pages/finance/FinanceFunds"));
const FinanceLearn = _l(() => import("@/pages/finance/FinanceLearn"));
const FinanceBlog = _l(() => import("@/pages/finance/FinanceBlog"));
const FinanceAuthor = _l(() => import("@/pages/finance/FinanceAuthor"));
const FinanceJobs = _l(() => import("@/pages/finance/FinanceJobs"));
const FinanceCourses = _l(() => import("@/pages/finance/FinanceCourses"));

// DGTG Website Pages
import DGTGHome from "@/pages/dgtg/DGTGHome";
const DGTGAbout = _l(() => import("@/pages/dgtg/DGTGAbout"));
const DGTGContact = _l(() => import("@/pages/dgtg/DGTGContact"));
const DGTGPartners = _l(() => import("@/pages/dgtg/DGTGPartners"));
const DGTGEventsCalendar = _l(() => import("@/pages/dgtg/DGTGEventsCalendar"));
const DGTGLegalPage = _l(() => import("@/pages/dgtg/DGTGLegal").then((m) => ({ default: m.DGTGLegalPage })));
const DGTGSitemap = _l(() => import("@/pages/dgtg/DGTGLegal").then((m) => ({ default: m.DGTGSitemap })));
const DGTGCampaignPage = _l(() => import("@/pages/dgtg/DGTGCampaignPage").then((m) => ({ default: m.DGTGCampaignPage })));
const DGTGCampaignIndex = _l(() => import("@/pages/dgtg/DGTGCampaignPage").then((m) => ({ default: m.DGTGCampaignIndex })));
const DGTGServiceDetail = _l(() => import("@/pages/dgtg/DGTGServiceDetail"));
const DGTGIndustry = _l(() => import("@/pages/dgtg/DGTGIndustry"));
const DGTGBlog = _l(() => import("@/pages/dgtg/DGTGBlog"));
const DGTGAuthor = _l(() => import("@/pages/dgtg/DGTGAuthor"));
const DGTGEstimate = _l(() => import("@/pages/dgtg/DGTGEstimate"));
const DGTGGrow = _l(() => import("@/pages/dgtg/DGTGGrow"));
const DGTGGrowIndex = _l(() => import("@/pages/dgtg/DGTGGrow").then(m => ({ default: m.DGTGGrowIndex })));

// GamesNFans Website Pages
import GamesHome from "@/pages/games/GamesHome";
const GamesCategory = _l(() => import("@/pages/games/GamesCategory"));
const GamesArticle = _l(() => import("@/pages/games/GamesArticle"));
const GamesShop = _l(() => import("@/pages/games/GamesShop"));
const GamesBuilder = _l(() => import("@/pages/games/GamesBuilder"));
const GamesGearAudience = _l(() => import("@/pages/games/GamesGearAudience"));
const GamesPlay = _l(() => import("@/pages/games/GamesPlay"));
const GamesPlayer = _l(() => import("@/pages/games/GamesPlayer"));
const GamesCreate = _l(() => import("@/pages/games/GamesCreate"));
const GamesPublish = _l(() => import("@/pages/games/GamesPublish"));
const GamesPrivacy = _l(() => import("@/pages/games/GamesPrivacy"));
const GamesTerms = _l(() => import("@/pages/games/GamesTerms"));
const GamesAbout = _l(() => import("@/pages/games/GamesAbout"));
const GamesDisclaimer = _l(() => import("@/pages/games/GamesDisclaimer"));
const GamesCricketer = _l(() => import("@/pages/games/GamesCricketer"));
const CricketerIndex = _l(() => import("@/pages/games/GamesCricketer").then(m => ({ default: m.CricketerIndex })));
const GamesIPL = _l(() => import("@/pages/games/GamesIPL"));
const IPLIndex = _l(() => import("@/pages/games/GamesIPL").then(m => ({ default: m.IPLIndex })));
const GamesLegacy = _l(() => import("@/pages/games/GamesLegacy"));
const GamesAuthor = _l(() => import("@/pages/games/GamesAuthor"));
const ClusterDetail = _l(() => import("@/pages/games/components/ClusterPage"));
const ClusterIndex = _l(() => import("@/pages/games/components/ClusterPage").then(m => ({ default: m.ClusterIndex })));
import { CLUSTER_CONFIGS } from "@/pages/games/data/phaseBClusters";

// Vikasifications Website Pages
import VikasHome from "@/pages/vikas/VikasHome";
const VikasBook = _l(() => import("@/pages/vikas/VikasBook"));
const VikasBlog = _l(() => import("@/pages/vikas/VikasBlog"));
const VikasAuthor = _l(() => import("@/pages/vikas/VikasAuthor"));
const VikasContact = _l(() => import("@/pages/vikas/VikasContact"));
const VikasWorkWithMe = _l(() => import("@/pages/vikas/VikasWorkWithMe"));
const VikasSpeaking = _l(() => import("@/pages/vikas/VikasSpeaking"));
const VikasPrivacy = _l(() => import("@/pages/vikas/VikasPrivacy"));
const VikasTerms = _l(() => import("@/pages/vikas/VikasTerms"));

// DevMob Website Pages
import DevmobHome from "@/pages/devmob/DevmobHome";
const DevmobServices = _l(() => import("@/pages/devmob/DevmobServices"));
const DevmobPortfolio = _l(() => import("@/pages/devmob/DevmobPortfolio"));
const DevmobAbout = _l(() => import("@/pages/devmob/DevmobAbout"));
const DevmobCareers = _l(() => import("@/pages/devmob/DevmobCareers"));
const DevmobContact = _l(() => import("@/pages/devmob/DevmobContact"));
const DevmobBlog = _l(() => import("@/pages/devmob/DevmobBlog"));
const DevmobAuthor = _l(() => import("@/pages/devmob/DevmobAuthor"));
const DevmobEstimate = _l(() => import("@/pages/devmob/DevmobEstimate"));
const DevmobBuild = _l(() => import("@/pages/devmob/DevmobBuild"));
const DevmobBuildIndex = _l(() => import("@/pages/devmob/DevmobBuild").then(m => ({ default: m.DevmobBuildIndex })));

// Auth
const AuthForgotPassword = _l(() => import("@/pages/auth/AuthForgotPassword"));

// User App
const UserDashboard = _l(() => import("@/pages/app/UserDashboard"));

// Affiliate
const AffiliateDashboard = _l(() => import("@/pages/affiliate/AffiliateDashboard"));
const AffiliateJoin = _l(() => import("@/pages/affiliate/AffiliateJoin"));

// Admin Pages — ALL lazy (only loaded when admin logs in)
const AdminLogin = _l(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = _l(() => import("@/pages/admin/AdminDashboard"));
const AdminVehicles = _l(() => import("@/pages/admin/AdminVehicles"));
const AdminCoupons = _l(() => import("@/pages/admin/AdminCoupons"));
const AdminLeads = _l(() => import("@/pages/admin/AdminLeads"));
const AdminAffiliates = _l(() => import("@/pages/admin/AdminAffiliates"));
const AdminAds = _l(() => import("@/pages/admin/AdminAds"));
const AdAnalytics = _l(() => import("@/pages/admin/AdAnalytics"));
const AdminManageAdmins = _l(() => import("@/pages/admin/AdminManageAdmins"));
const AdminOverview = _l(() => import("@/pages/admin/AdminOverview"));
const AdminAffiliatePrograms = _l(() => import("@/pages/admin/AdminAffiliatePrograms"));
const AdminSystem = _l(() => import("@/pages/admin/AdminSystem"));
const AdminDatasets = _l(() => import("@/pages/admin/AdminDatasets"));
const AdminOffers = _l(() => import("@/pages/admin/AdminOffers"));
const AdminBuildLog = _l(() => import("@/pages/admin/AdminBuildLog"));
const AdminPlans = _l(() => import("@/pages/admin/AdminPlans"));
const AdminKeywords = _l(() => import("@/pages/admin/AdminKeywords"));
const AboutUs = _l(() => import("@/pages/legal/LegalPages").then(m => ({ default: m.AboutUs })));
const ContactUs = _l(() => import("@/pages/legal/LegalPages").then(m => ({ default: m.ContactUs })));
const TermsOfUse = _l(() => import("@/pages/legal/LegalPages").then(m => ({ default: m.TermsOfUse })));
const PrivacyPolicy = _l(() => import("@/pages/legal/LegalPages").then(m => ({ default: m.PrivacyPolicy })));
const Sitemap = _l(() => import("@/pages/legal/Sitemap"));
const Disclaimer = _l(() => import("@/pages/legal/Disclaimer"));
const ApiDocs = _l(() => import("@/pages/ApiDocs"));
const FreeRecharge = _l(() => import("@/pages/coupon/FreeRecharge"));
const CouponCityPage = _l(() => import("@/pages/coupon/CouponProgramPages").then(m => ({ default: m.CouponCityPage })));
const CouponStorePage = _l(() => import("@/pages/coupon/CouponProgramPages").then(m => ({ default: m.CouponStorePage })));
const CouponCitiesIndex = _l(() => import("@/pages/coupon/CouponProgramPages").then(m => ({ default: m.CouponCitiesIndex })));
const CouponStoresIndex = _l(() => import("@/pages/coupon/CouponProgramPages").then(m => ({ default: m.CouponStoresIndex })));
const CouponCityCategoryPage = _l(() => import("@/pages/coupon/CouponProgramPages").then(m => ({ default: m.CouponCityCategoryPage })));
const AdminRecharge = _l(() => import("@/pages/admin/AdminRecharge"));
const AdminSubmissions = _l(() => import("@/pages/admin/AdminSubmissions"));
const AdminContent = _l(() => import("@/pages/admin/AdminContent"));
const AdminTrends = _l(() => import("@/pages/admin/AdminTrends"));
const AdminSEO = _l(() => import("@/pages/admin/AdminSEO"));
const AdminClients = _l(() => import("@/pages/admin/AdminClients"));
const ClientDashboard = _l(() => import("@/pages/app/ClientDashboard"));
const LeadAnalytics = _l(() => import("@/pages/admin/LeadAnalytics"));

import NotificationBanner from "@/components/NotificationBanner";
import BookmarkHint from "@/components/BookmarkHint";
import AskWidget from "@/components/AskWidget";

// Fallback while a route chunk downloads
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="route-loading">
      <div className="animate-pulse text-gray-400 text-sm">Loading…</div>
    </div>
  );
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
    // Reset the per-page ad budget so every route starts with a full 5-slot allowance
    // (spans <AdSlot> React components AND inline .ad-slot blocks in blog markdown).
    resetAdBudget();
  }, [pathname]);
  return null;
}

// Redirect prefixed URLs to their stripped equivalent on matching production
// domains. Fixes the case where a user lands on `metromandi.com/coupons`
// (from an external link or NetworkFooter) but routes are stripped so nothing
// matches. Runs client-side once per route change, replacing history entry
// so back-button behaviour stays sensible.
function SitePrefixNormalizer() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    const sid = siteConfig.siteId;
    if (!sid || sid === 'all') return;
    const prefix = SITE_ROUTE_PREFIX[sid];
    if (!prefix) return;
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      const stripped = pathname.slice(prefix.length) || '/';
      navigate(stripped + search + hash, { replace: true });
    }
  }, [pathname, search, hash, navigate]);
  return null;
}

// Route path translator — strips site prefix on matching production domain
const SITE_ROUTE_PREFIX = {
  games: '/games', dgtg: '/dgtg', devmob: '/devmob', vikas: '/vikas',
  auto: '/auto', coupon: '/coupons', finance: '/finance',
  travel: '/travel', tech: '/tech',
};
function routePath(p) {
  const sid = siteConfig.siteId;
  if (sid && sid !== 'all') {
    const prefix = SITE_ROUTE_PREFIX[sid];
    if (prefix && (p === prefix || p.startsWith(prefix + '/'))) {
      return p.slice(prefix.length) || '/';
    }
  }
  return p;
}


// metromandi.com/<coupon-uuid> was the old sitemap URL for a coupon; the page lives at /deal/<id>.
const COUPON_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function LegacyCouponRedirect() {
  const { legacyCouponId } = useParams();
  return COUPON_ID_RE.test(legacyCouponId || '') ? <Navigate to={`/deal/${legacyCouponId}`} replace /> : null;
}

// Dynamic favicon and title based on detected site or route
// Single source of truth for React-side favicon switching (preview "all" mode,
// where the hostname-based inline script in index.html can't tell sites apart —
// it only fires once per hostname load, not on in-app route changes).
const SITE_FAVICONS = {
  auto: '/favicon-auto-32.png',
  coupon: '/favicon-coupon-32.png',
  finance: '/favicon-finance-32.png',
  admin: '/favicon-admin-32.png',
  dgtg: '/favicon-dgtg-32.png',
  games: '/favicon-games-32.png',
  vikas: '/favicon-vikas-32.png',
  devmob: '/favicon-devmob-32.png',
  travel: '/favicon-travel-32.png',
  tech: '/favicon-tech-32.png',
};
const SITE_TITLES = {
  auto: 'CarsbikesPrices, Vehicle Prices & Reviews',
  coupon: 'MetroMandi, Deals, Coupons & Savings',
  finance: 'YourMoneyWise, Calculators, Compare & Learn',
  admin: 'AllMarketingMixed, Admin Dashboard',
  dgtg: 'DGTG, Community-Powered Digital Growth Agency',
  games: 'GamesNFans, Esports, Gaming & Cricket',
  vikas: 'Vikasifications, Vikas Goel',
  devmob: 'DevMob, Technology Services',
};

function DynamicFavicon() {
  const location = useLocation();
  React.useEffect(() => {
    let siteId = siteConfig.siteId;
    let matchedPrefix = true;
    // Fallback to path-based detection for preview/all mode
    if (siteId === 'all') {
      const p = location.pathname;
      if (p.startsWith('/finance')) siteId = 'finance';
      else if (p.startsWith('/auto')) siteId = 'auto';
      else if (p.startsWith('/coupons') || p.startsWith('/free-recharge')) siteId = 'coupon';
      else if (p.startsWith('/admin')) siteId = 'admin';
      else if (p.startsWith('/dgtg')) siteId = 'dgtg';
      else if (p.startsWith('/games')) siteId = 'games';
      else if (p.startsWith('/vikas')) siteId = 'vikas';
      else if (p.startsWith('/devmob')) siteId = 'devmob';
      else if (p.startsWith('/travel')) siteId = 'travel';
      else if (p.startsWith('/tech')) siteId = 'tech';
      else { siteId = 'auto'; matchedPrefix = false; }
    }
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    const href = SITE_FAVICONS[siteId] || SITE_FAVICONS.admin;
    link.type = href.endsWith('.png') ? 'image/png' : 'image/svg+xml';
    link.href = href;
    // Track which site section the user is in (for legal page breadcrumbs in "all" mode).
    // Only overwrite when the path gave a confident prefix match — path-less pages like
    // /about, /privacy, /terms, /disclaimer, /contact must NOT clobber the previously
    // remembered site (they'd otherwise all fall back to the 'auto' default above).
    if (siteConfig.siteId === 'all' ? matchedPrefix : true) {
      try { sessionStorage.setItem('_site_entry', siteId); } catch {}
    }
    // Count the pageview in our own database. The admin panel had no traffic numbers at all because
    // articles were never counted anywhere; GA4 only covers four of the ten sites. The admin console
    // itself is excluded: our own editing is not audience traffic.
    if (siteId !== 'admin') trackPageview(siteId, location.pathname);
    // Title is managed per-page via react-helmet-async <Helmet><title>
  }, [location.pathname]);
  return null;
}

// Cross-domain redirect component — passes encoded auth token if logged in
function CrossDomainRedirect({ to }) {
  if (to.startsWith('http')) {
    const token = getToken();
    window.location.href = token ? buildAuthUrl(to, token) : to;
    return null;
  }
  return <Navigate to={to} replace />;
}

function ChatOverlay() {
  const location = useLocation();
  const path = location.pathname;
  // Price-drop and coupon alerts belong to CarsbikesPrices and MetroMandi only. The old `path === '/'`
  // matched every site's homepage on its own domain, so an agency (DGTG) and a personal site offered
  // "price drops, new coupons and exclusive deals".
  const sid = siteConfig.siteId;
  const showNotif = sid === 'auto' || sid === 'coupon'
    || (sid === 'all' && (path.startsWith('/auto') || path.startsWith('/coupons')));
  // Ask-and-capture on every consumer site (not admin): answers from our own articles, then captures the lead.
  const isAdminArea = path.startsWith('/admin') || path.startsWith('/app') || path.startsWith('/affiliate');
  return (
    <>
      {showNotif && <NotificationBanner />}
      <BookmarkHint />
      {!isAdminArea && siteConfig.siteId !== 'admin' && <AskWidget />}
    </>
  );
}

function App() {
  const { showAutoRoutes, showCouponRoutes, showFinanceRoutes, showAdminRoutes, showDgtgRoutes, showGamesRoutes, showVikasRoutes, showDevmobRoutes, showTravelRoutes, showTechRoutes, defaultRoute } = siteConfig;

  React.useEffect(() => {
    // Clear the one-shot chunk-retry guard once the app has been stable for a
    // few seconds, so a genuine later chunk failure still gets one retry too.
    const t = setTimeout(() => sessionStorage.removeItem('chunk-retry-once'), 8000);
    return () => clearTimeout(t);
  }, []);

  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <SitePrefixNormalizer />
        <DynamicFavicon />
        <RegionBanner />
        <CookieConsent />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Landing, on preview ('all') and admin (allmarketingmixed.com) redirect to default; consumer domains (dgtg, games, etc.) mount their home at '/' directly via routePath() */}
          {(siteConfig.siteId === 'all' || siteConfig.siteId === 'admin') && (
            <Route path="/" element={<Navigate to={defaultRoute} replace />} />
          )}

          {/* ===== AUTO ROUTES (carsbikesprices.com) ===== */}
          {showAutoRoutes ? (
            <>
              <Route path={routePath("/auto")} element={<AutoHome />} />
              <Route path={routePath("/auto/listings")} element={<VehicleListings />} />
              <Route path={routePath("/auto/vehicle/:id")} element={<VehicleDetail />} />
              <Route path={routePath("/auto/compare")} element={<CompareVehicles />} />
              <Route path={routePath("/auto/price-pulse")} element={<AutoPricePulse />} />
              <Route path={routePath("/auto/registrations")} element={<AutoRegistrations />} />
              <Route path={routePath("/auto/news")} element={<AutoNews />} />
              <Route path={routePath("/auto/blog")} element={<AutoBlog />} />
              <Route path={routePath("/auto/blog/:articleId")} element={<AutoBlog />} />
              <Route path={routePath("/auto/author/:slug")} element={<AutoAuthor />} />
              <Route path={routePath("/auto/sell")} element={<SubmitVehicle />} />
            </>
          ) : (
            <Route path={routePath("/auto/*")} element={<CrossDomainRedirect to={siteConfig.autoUrl()} />} />
          )}

          {/* ===== COUPON ROUTES (metromandi.com) ===== */}
          {showCouponRoutes ? (
            <>
              <Route path={routePath("/coupons")} element={<CouponHome />} />
              <Route path={routePath("/coupons/browse")} element={<CouponListings />} />
              <Route path={routePath("/coupons/browse/:category")} element={<CouponListings />} />
              <Route path={routePath("/coupons/deal/:id")} element={<CouponDetail />} />
              <Route path={routePath("/coupons/live-deals")} element={<LiveDeals />} />
              <Route path={routePath("/coupon/blog")} element={<CouponBlog />} />
              <Route path={routePath("/coupon/blog/:articleId")} element={<CouponBlog />} />
              {/* Also match the un-stripped paths so /blog and /blog/:slug work directly on metromandi.com */}
              {siteConfig.siteId === 'coupon' && (
                <>
                  <Route path="/blog" element={<CouponBlog />} />
                  <Route path="/blog/:articleId" element={<CouponBlog />} />
                  <Route path="/:legacyCouponId" element={<LegacyCouponRedirect />} />
                </>
              )}
              <Route path={routePath("/coupons/free-recharge")} element={<FreeRecharge />} />
              <Route path={routePath("/coupons/city")} element={<CouponCitiesIndex />} />
              <Route path={routePath("/coupons/city/:citySlug")} element={<CouponCityPage />} />
              <Route path={routePath("/coupons/city/:citySlug/category/:categorySlug")} element={<CouponCityCategoryPage />} />
              <Route path={routePath("/coupons/store")} element={<CouponStoresIndex />} />
              <Route path={routePath("/coupons/store/:storeSlug")} element={<CouponStorePage />} />
            </>
          ) : (
            <Route path={routePath("/coupons/*")} element={<CrossDomainRedirect to={siteConfig.couponUrl()} />} />
          )}

          {/* ===== TRAVEL ROUTES (woketraveler.com) ===== */}
          {showTravelRoutes ? (
            <>
              {/* Sep 2026: WokeTraveler = US/UK/EU travel aggregator (search, route pages, destinations) + guides */}
              <Route path={routePath("/travel")} element={<TravelHome />} />
              <Route path={routePath("/travel/trains")} element={<TravelRoutesIndex />} />
              <Route path={routePath("/travel/routes/:routeSlug")} element={<TravelRoute />} />
              <Route path={routePath("/travel/destinations")} element={<TravelDestinationsIndex />} />
              <Route path={routePath("/travel/destinations/:citySlug")} element={<TravelDestination />} />
              <Route path={routePath("/travel/flights")} element={<TravelModePage mode="flight" />} />
              <Route path={routePath("/travel/hotels")} element={<TravelModePage mode="hotel" />} />
              <Route path={routePath("/travel/blog")} element={<TravelBlog />} />
              <Route path={routePath("/travel/blog/:articleId")} element={<TravelBlog />} />
              <Route path={routePath("/travel/author/:slug")} element={<TravelAuthor />} />
              {/* Also match the un-stripped paths so both /travel/blog and /blog work on woketraveler.com */}
              {siteConfig.siteId === 'travel' && (
                <>
                  <Route path="/travel" element={<TravelHome />} />
                  <Route path="/travel/blog" element={<TravelBlog />} />
                  <Route path="/travel/blog/:articleId" element={<TravelBlog />} />
                </>
              )}
            </>
          ) : (
            <Route path={routePath("/travel/*")} element={<CrossDomainRedirect to={siteConfig.travelUrl()} />} />
          )}

          {/* ===== TECH ROUTES (tech-n-ai-talks.com) ===== */}
          {showTechRoutes ? (
            <>
              <Route path={routePath("/tech")} element={<TechBlog />} />
              <Route path={routePath("/tech/blog")} element={<TechBlog />} />
              <Route path={routePath("/tech/blog/:articleId")} element={<TechBlog />} />
              <Route path={routePath("/tech/author/:slug")} element={<TechAuthor />} />
              {/* Also match un-stripped paths */}
              {siteConfig.siteId === 'tech' && (
                <>
                  <Route path="/tech" element={<TechBlog />} />
                  <Route path="/tech/blog" element={<TechBlog />} />
                  <Route path="/tech/blog/:articleId" element={<TechBlog />} />
                </>
              )}
            </>
          ) : (
            <Route path={routePath("/tech/*")} element={<CrossDomainRedirect to={siteConfig.techUrl()} />} />
          )}

          {/* ===== FINANCE ROUTES (yourmoneywise.net) ===== */}
          {showFinanceRoutes ? (
            <>
              <Route path={routePath("/finance")} element={<FinanceHome />} />
              <Route path={routePath("/finance/calculators")} element={<FinanceCalculators />} />
              <Route path={routePath("/finance/calculators/:type")} element={<FinanceCalculators />} />
              <Route path={routePath("/finance/compare")} element={<FinanceCompare />} />
              <Route path={routePath("/finance/funds")} element={<FinanceFunds />} />
              <Route path={routePath("/finance/learn")} element={<FinanceLearn />} />
              <Route path={routePath("/finance/learn/:moduleId")} element={<FinanceLearn />} />
              <Route path={routePath("/finance/blog")} element={<FinanceBlog />} />
              <Route path={routePath("/finance/blog/:slug")} element={<FinanceBlog />} />
              <Route path={routePath("/finance/author/:slug")} element={<FinanceAuthor />} />
              <Route path={routePath("/finance/jobs")} element={<FinanceJobs />} />
              <Route path={routePath("/finance/jobs/:jobId")} element={<FinanceJobs />} />
              <Route path={routePath("/finance/courses")} element={<FinanceCourses />} />
            </>
          ) : (
            <Route path={routePath("/finance/*")} element={<CrossDomainRedirect to={siteConfig.financeUrl()} />} />
          )}

          {/* ===== DGTG ROUTES (dgtg.in) ===== */}
          {showDgtgRoutes ? (
            <>
              <Route path={routePath("/dgtg")} element={<DGTGHome />} />
              <Route path={routePath("/dgtg/about")} element={<DGTGAbout />} />
              <Route path={routePath("/dgtg/contact")} element={<DGTGContact />} />
              <Route path={routePath("/dgtg/partners")} element={<DGTGPartners />} />
              <Route path={routePath("/dgtg/events")} element={<DGTGEventsCalendar />} />
              {/* DGTG's own legal pages and sitemap. Declared before the shared /privacy, /terms, /disclaimer
                  and /sitemap routes further down, so on dgtg.in these win. */}
              <Route path={routePath("/dgtg/privacy")} element={<DGTGLegalPage kind="privacy" />} />
              <Route path={routePath("/dgtg/terms")} element={<DGTGLegalPage kind="terms" />} />
              <Route path={routePath("/dgtg/disclaimer")} element={<DGTGLegalPage kind="disclaimer" />} />
              <Route path={routePath("/dgtg/cookies")} element={<DGTGLegalPage kind="cookies" />} />
              <Route path={routePath("/dgtg/sitemap")} element={<DGTGSitemap />} />
              {/* The world-data globe was replaced by "How we work" (2026-09-28); old links land there. */}
              <Route path={routePath("/dgtg/data/*")} element={<Navigate to={siteLink("/dgtg#how-we-work")} replace />} />
              <Route path={routePath("/dgtg/campaigns")} element={<DGTGCampaignIndex />} />
              <Route path={routePath("/dgtg/campaigns/:slug")} element={<DGTGCampaignPage />} />
              <Route path={routePath("/dgtg/service/:serviceId")} element={<DGTGServiceDetail />} />
              <Route path={routePath("/dgtg/industry/:industrySlug")} element={<DGTGIndustry />} />
              <Route path={routePath("/dgtg/blog")} element={<DGTGBlog />} />
              <Route path={routePath("/dgtg/blog/:articleId")} element={<DGTGBlog />} />
              <Route path={routePath("/dgtg/author/:slug")} element={<DGTGAuthor />} />
              <Route path={routePath("/dgtg/estimate")} element={<DGTGEstimate />} />
              <Route path={routePath("/dgtg/grow")} element={<DGTGGrowIndex />} />
              <Route path={routePath("/dgtg/grow/:topic")} element={<DGTGGrow />} />
              {/* The same pages in Arabic, Chinese, Portuguese, Spanish and French: /dgtg/<lang>/... (on dgtg.in, /<lang>/...).
                  Blog, legal pages, world data and the calculator stay English. See src/i18n/dgtgLang.js. */}
              {LANG_CODES.flatMap((lg) => [
                ['', <DGTGHome />], ['/about', <DGTGAbout />], ['/contact', <DGTGContact />], ['/partners', <DGTGPartners />], ['/events', <DGTGEventsCalendar />],
                ['/service/:serviceId', <DGTGServiceDetail />], ['/industry/:industrySlug', <DGTGIndustry />],
                ['/campaigns', <DGTGCampaignIndex />], ['/campaigns/:slug', <DGTGCampaignPage />],
                ['/grow', <DGTGGrowIndex />], ['/grow/:topic', <DGTGGrow />],
              ].map(([sub, el]) => (
                <Route key={`${lg}${sub}`} path={routePath(`/dgtg/${lg}${sub}`)} element={<DGTGLangScope lang={lg}>{el}</DGTGLangScope>} />
              )))}
            </>
          ) : (
            <Route path={routePath("/dgtg/*")} element={<CrossDomainRedirect to={siteConfig.dgtgUrl()} />} />
          )}


          {/* ===== GAMESNFANS ROUTES (gamesnfans.tv) ===== */}
          {showGamesRoutes ? (
            <>
              <Route path={routePath("/games")} element={<GamesHome />} />
              <Route path={routePath("/games/article/:articleId")} element={<GamesArticle />} />
              <Route path={routePath("/games/blog/:articleId")} element={<GamesArticle />} />
              <Route path={routePath("/games/author/:slug")} element={<GamesAuthor />} />
              {/* Shop pillar */}
              <Route path={routePath("/games/shop")} element={<GamesShop />} />
              <Route path={routePath("/games/shop/:categorySlug")} element={<GamesShop />} />
              <Route path={routePath("/games/builder")} element={<GamesBuilder />} />
              <Route path={routePath("/games/gear/:audience")} element={<GamesGearAudience />} />
              <Route path={routePath("/games/gear/:audience/:slug")} element={<GamesGearAudience />} />
              {/* Play pillar */}
              <Route path={routePath("/games/play")} element={<GamesPlay />} />
              <Route path={routePath("/games/play/:slug")} element={<GamesPlayer />} />
              {/* Create + Publish */}
              <Route path={routePath("/games/create")} element={<GamesCreate />} />
              <Route path={routePath("/games/publish")} element={<GamesPublish />} />
              {/* Dedicated legal pages — must be declared before the shared /privacy /terms
                  fallback below so they take precedence on the production games domain
                  (routePath strips "/games" here, matching the shared route's literal path;
                  first-declared wins). Content is GamesNFans-specific, not the network-wide
                  vehicle/coupon/finance boilerplate used by the shared LegalPages.js. */}
              <Route path={routePath("/games/privacy")} element={<GamesPrivacy />} />
              <Route path={routePath("/games/terms")} element={<GamesTerms />} />
              <Route path={routePath("/games/about")} element={<GamesAbout />} />
              <Route path={routePath("/games/disclaimer")} element={<GamesDisclaimer />} />
              {/* Programmatic clusters */}
              <Route path={routePath("/games/cricketer")} element={<CricketerIndex />} />
              <Route path={routePath("/games/cricketer/:slug")} element={<GamesCricketer />} />
              <Route path={routePath("/games/ipl")} element={<IPLIndex />} />
              <Route path={routePath("/games/ipl/:slug")} element={<GamesIPL />} />
              {/* Phase B programmatic clusters, loadouts, guides, tournaments, game-guides, watch, anime, franchise.
                  Sep 2026: wrapped in routePath(); unwrapped they only matched /games/<cluster>, which
                  gamesnfans.tv 308-redirects away, so every cluster page rendered blank on the live domain. */}
              {Object.entries(CLUSTER_CONFIGS).map(([key, cfg]) => (
                <Route key={`idx-${key}`} path={routePath(cfg.basePath)} element={<ClusterIndex config={cfg} />} />
              ))}
              {Object.entries(CLUSTER_CONFIGS).map(([key, cfg]) => (
                <Route key={`det-${key}`} path={routePath(`${cfg.basePath}/:slug`)} element={<ClusterDetail config={cfg} />} />
              ))}
              {/* Content category (catch-all, MUST be last) */}
              <Route path={routePath("/games/:category")} element={<GamesCategory />} />
            </>
          ) : (
            <Route path={routePath("/games/*")} element={<CrossDomainRedirect to={siteConfig.gamesUrl()} />} />
          )}

          {/* ===== LEGACY ROOT-LEVEL SLUG CATCH-ALL (gamesnfans.tv/<slug>/) =====
              Only active on gamesnfans (showGamesRoutes). Handles WP legacy URLs:
              - Spam slugs -> 410 Gone
              - Known clusters -> in-place programmatic page with new canonical
              - Unknown -> graceful 404 with navigation
              This must be LAST so all other routes win first. */}
          {showGamesRoutes && (
            <Route path="/:legacySlug" element={<GamesLegacy />} />
          )}

          {/* ===== VIKASIFICATIONS ROUTES (vikasifications.com) ===== */}
          {showVikasRoutes && (
            <>
              <Route path={routePath("/vikas")} element={<VikasHome />} />
              <Route path={routePath("/vikas/book")} element={<VikasBook />} />
              <Route path={routePath("/vikas/blog")} element={<VikasBlog />} />
              <Route path={routePath("/vikas/blog/:articleId")} element={<VikasBlog />} />
              <Route path={routePath("/vikas/author/:slug")} element={<VikasAuthor />} />
              <Route path={routePath("/vikas/speaking")} element={<VikasSpeaking />} />
              <Route path={routePath("/vikas/privacy")} element={<VikasPrivacy />} />
              <Route path={routePath("/vikas/terms")} element={<VikasTerms />} />
              <Route path={routePath("/vikas/contact")} element={<VikasContact />} />
              <Route path={routePath("/vikas/build-log")} element={<VikasBlog initialFilter="build" />} />
              <Route path={routePath("/vikas/work-with-me")} element={<VikasWorkWithMe />} />
            </>
          )}

          {/* ===== DEVMOB ROUTES (devmob.co) ===== */}
          {showDevmobRoutes ? (
            <>
              <Route path={routePath("/devmob")} element={<DevmobHome />} />
              <Route path={routePath("/devmob/services")} element={<DevmobServices />} />
              <Route path={routePath("/devmob/portfolio")} element={<DevmobPortfolio />} />
              <Route path={routePath("/devmob/about")} element={<DevmobAbout />} />
              <Route path={routePath("/devmob/careers")} element={<DevmobCareers />} />
              <Route path={routePath("/devmob/contact")} element={<DevmobContact />} />
              <Route path={routePath("/devmob/blog")} element={<DevmobBlog />} />
              <Route path={routePath("/devmob/blog/:articleId")} element={<DevmobBlog />} />
              <Route path={routePath("/devmob/author/:slug")} element={<DevmobAuthor />} />
              <Route path={routePath("/devmob/estimate")} element={<DevmobEstimate />} />
              <Route path={routePath("/devmob/build")} element={<DevmobBuildIndex />} />
              <Route path={routePath("/devmob/build/:topic")} element={<DevmobBuild />} />
            </>
          ) : (
            <Route path={routePath("/devmob/*")} element={<CrossDomainRedirect to={siteConfig.devmobUrl()} />} />
          )}

          {/* ===== ADMIN ROUTES (allmarketingmixed.com) ===== */}
          {showAdminRoutes ? (
            <>
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/forgot-password" element={<AuthForgotPassword />} />
              <Route path="/admin/overview" element={<AdminOverview />} />
              <Route path="/admin/affiliate-programs" element={<AdminAffiliatePrograms />} />
              <Route path="/admin/system" element={<AdminSystem />} />
              <Route path="/admin/datasets" element={<AdminDatasets />} />
              <Route path="/admin/offers" element={<AdminOffers />} />
              <Route path="/admin/build-log" element={<AdminBuildLog />} />
              <Route path="/admin/plans" element={<AdminPlans />} />
              <Route path="/admin/keywords" element={<AdminKeywords />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/vehicles" element={<AdminVehicles />} />
              <Route path="/admin/coupons" element={<AdminCoupons />} />
              <Route path="/admin/leads" element={<AdminLeads />} />
              <Route path="/admin/leads/analytics" element={<LeadAnalytics />} />
              <Route path="/admin/affiliates" element={<AdminAffiliates />} />
              <Route path="/admin/ads" element={<AdminAds />} />
              <Route path="/admin/ads/analytics" element={<AdAnalytics />} />
              <Route path="/admin/recharge" element={<AdminRecharge />} />
              <Route path="/admin/submissions" element={<AdminSubmissions />} />
              <Route path="/admin/content" element={<AdminContent />} />
              <Route path="/admin/trends" element={<AdminTrends />} />
              {/* The one-at-a-time sources queue was replaced by the sweep on the SEO page (11 Oct 2026). */}
              <Route path="/admin/citations" element={<Navigate to="/admin/seo" replace />} />
              <Route path="/admin/seo" element={<AdminSEO />} />
              <Route path="/admin/clients" element={<AdminClients />} />
              <Route path="/app/client" element={<ClientDashboard />} />
              <Route path="/admin/manage-admins" element={<AdminManageAdmins />} />
              <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
              <Route path="/affiliate/join" element={<AffiliateJoin />} />
              <Route path="/app/dashboard" element={<UserDashboard />} />
            </>
          ) : (
            <>
              <Route path="/admin/*" element={<CrossDomainRedirect to={siteConfig.adminUrl()} />} />
              <Route path="/affiliate/*" element={<CrossDomainRedirect to={siteConfig.adminUrl()} />} />
            </>
          )}

          {/* Auth redirects — always point to admin site */}
          <Route path="/auth/login" element={<CrossDomainRedirect to={siteConfig.adminUrl('/admin')} />} />
          <Route path="/auth/register" element={<CrossDomainRedirect to={siteConfig.adminUrl('/admin')} />} />
          <Route path="/auth/forgot-password" element={showAdminRoutes ? <AuthForgotPassword /> : <CrossDomainRedirect to={siteConfig.adminUrl('/admin')} />} />

          {/* Public affiliate join page, available on all sites */}
          <Route path="/affiliate/join" element={<AffiliateJoin />} />

          {/* Legal / Static Pages, available on all sites */}
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/developer/api" element={<ApiDocs />} />
          <Route path="/sitemap" element={<Sitemap />} />
        </Routes>
        </Suspense>
        <ChatOverlay />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;

